import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaBan,
  FaChartBar,
  FaCopy,
  FaExternalLinkAlt,
  FaFileAlt,
  FaLink,
  FaPaperPlane,
  FaPlus,
  FaRedo,
  FaSave,
  FaTrash,
} from "react-icons/fa";
import {
  createConsentFormApi,
  updateConsentFormApi,
  fetchConsentFormsApi,
  publishConsentFormApi,
  generateConsentLinkApi,
  fetchConsentLinksApi,
  revokeConsentLinkApi,
  fetchConsentSubmissionsApi,
  getPublicBaseUrl,
  publicConsentUrl,
} from "./consentApi";
import Menubar from "../Menubar";
import NavigationBar from "../Nav";
import BackgroundFooter from "../BackgroundFooter";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../ComponentsCss/Iam.css";

const TABS = {
  FORMS: "FORMS",
  LINKS: "LINKS",
  DASHBOARD: "DASHBOARD",
};

const FIELD_TYPES = [
  "text",
  "email",
  "tel",
  "date",
  "number",
  "textarea",
  "dropdown",
  "radio",
  "checkbox",
];

const createDefaultSchema = () => ({
  organizationBranding: {
    logoFileName: "",
    logoBase64: "",
    legalEntityName: "",
    cin: "",
    registeredAddress: "",
    website: "",
    enquiryEmail: "",
    enquiryPhone: "",
  },

  notice: {
    title: "Consent Form for Data Processing",
    regulation: "Digital Personal Data Protection Act, 2023",
    rules: "DPDP Rules, 2025",
    noticeVersion: "2.0",
    formVersion: "2.0",
  },

  informationCollected: {
    enabled: true,
    dataCategory: "Personal Data",
    sourceOfCollection: "Directly from you",
    availableDataCategories: [
      "Personal Data",
      "Financial Data",
      "Employment Data",
      "Biometric Data",
      "Children's Data",
      "Device / Usage Data",
    ],
    availableSources: [
      "Directly from you",
      "Employer or principal",
      "Partners and affiliates with prior consent",
      "Publicly available sources",
      "Automated systems",
      "Government or regulatory bodies",
    ],
  },

  legalBasis: {
    enabled: true,
    basis: "Consent-based processing under Section 6(1)(a)",
    availableBasis: [
      "Consent-based processing under Section 6(1)(a)",
      "Legitimate use under Section 7",
      "Legal obligation",
      "Employment purpose",
      "Emergency or medical purpose",
    ],
    description:
      "Optional purposes require explicit, free, specific, informed, and unambiguous consent. Mandatory purposes may be processed where required for service delivery or legal compliance.",
  },

  retention: {
    enabled: true,
    defaultRetentionPeriod: "",
    retentionBasis: "",
  },

  thirdPartyRecipients: {
    enabled: false,
    recipientCategory: "",
    purpose: "",
    dataShared: "",
    availableRecipientCategories: [
      "Cloud Service Providers",
      "Payment Processors",
      "HR and Payroll Vendors",
      "Marketing Platforms",
      "Cybersecurity Providers",
      "Professional Advisors",
      "Government / Regulatory Bodies",
      "No third-party sharing",
    ],
  },

  childConsent: {
    enabled: false,
    minorAgeThreshold: 18,
    requireGuardianConsent: true,
    guardianNameLabel: "Parent / Guardian Name",
    guardianContactLabel: "Guardian Contact Number",
    relationshipOptions: ["Father", "Mother", "Legal Guardian"],
    guardianDeclarationText:
      "I confirm that I am the parent/legal guardian and provide verifiable parental consent for processing the child's personal data.",
  },

 consentDeclaration: {
  enabled: true,
  mandatory: true,
  declarationText:
    "I confirm that I have read and understood the Privacy Notice. I understand the information provided and I voluntarily provide my free, specific, informed, unconditional, and unambiguous consent for the purpose(s) I have ticked in Section 4. I have not been coerced, misled, or offered any inducement to provide this consent. I understand that withdrawal of mandatory consent may affect service delivery, and that prior lawful processing remains valid after withdrawal.",
},

  withdrawal: {
  enabled: true,
  allowWithdrawAll: true,
  allowWithdrawOptional: true,
  allowWithdrawSpecific: true,
  withdrawalText:
    "The Data Principal may withdraw consent at any time by withdrawing all consent, withdrawing optional consent, or withdrawing consent for a specific purpose. Prior lawful processing remains valid after withdrawal. Withdrawal of mandatory consent may affect service delivery.",
},

  sections: [
    {
      key: "DATA_PRINCIPAL",
      title: "Data Principal Information",
      enabled: true,
      fields: [
        {
          id: "fullName",
          label: "Full Name",
          type: "text",
          required: true,
          sensitive: false,
          placeholder: "Enter your full name",
        },
        {
          id: "dob",
          label: "Date of Birth",
          type: "date",
          required: true,
          sensitive: false,
          placeholder: "",
        },
        {
          id: "email",
          label: "Email Address",
          type: "email",
          required: true,
          sensitive: false,
          placeholder: "Enter your email address",
        },
        {
          id: "mobile",
          label: "Mobile Number",
          type: "tel",
          required: true,
          sensitive: false,
          placeholder: "Enter your mobile number",
        },
        {
          id: "residentialAddress",
          label: "Residential Address",
          type: "textarea",
          required: true,
          sensitive: true,
          placeholder: "Enter your residential address",
        },
        {
          id: "aadhaarPanLast4",
          label: "Aadhaar / PAN Last 4 Digits",
          type: "text",
          required: false,
          sensitive: true,
          placeholder: "Last 4 digits only",
        },
      ],
    },
  ],

  purposes: [
    {
      id: "service_provision",
      name: "Service Provision",
      description:
        "Deliver requested services, manage account, and provide customer support.",
      mandatory: true,
      defaultEnabled: true,
    },
    {
      id: "legal_compliance",
      name: "Legal Compliance",
      description:
        "Comply with applicable laws, court orders, and regulatory obligations.",
      mandatory: true,
      defaultEnabled: true,
    },
    {
      id: "marketing",
      name: "Marketing",
      description: "Send promotional materials, offers, newsletters, and updates.",
      mandatory: false,
      defaultEnabled: false,
    },
    {
      id: "analytics",
      name: "Analytics",
      description: "Analyse usage to improve services and user experience.",
      mandatory: false,
      defaultEnabled: false,
    },
  ],

  consentMetadata: {
    methodOptions: ["Digital", "Physical", "Verbal"],
    defaultMethod: "Digital",
    sourceApplication: "SUSAN Consent Management",
  },

  auditRecord: {
    enabled: true,
    captureIpAddress: true,
    captureUserAgent: true,
    captureTimestamp: true,
    captureNoticeVersion: true,
    captureFormVersion: true,
  },

  noThirdPartySharing: true,
});

const emptyForm = {
  formName: "",
  regulation: "DPDP",
  businessProcess: "",
  dataCollectionPurpose: "",
  formVersion: "1.0",
  noticeVersion: "1.0",
};

const getOrganizationId = (profile, userData) => {
  return (
    profile?.orgId ||
    userData?.organizationId ||
    userData?.orgId ||
    userData?.["custom:orgId"] ||
    userData?.email ||
    "org-123"
  );
};

const ConsentManagementModule = ({ userData, profile, showNotification }) => {
  const [activeTab, setActiveTab] = useState(TABS.FORMS);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false;
  });

  useEffect(() => {
    localStorage.setItem(
      "isSidebarCollapsed",
      JSON.stringify(isSidebarCollapsed)
    );
  }, [isSidebarCollapsed]);

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed((prevState) => !prevState);
  };

  React.useEffect(() => {
    console.log("🟢 CMM real mount");
    console.trace("CMM mount stack");   // ← this prints the component tree path
    return () => console.log("🔴 CMM unmount");
  }, []);
  const organizationId = useMemo(
    () => getOrganizationId(profile, userData),
    [profile, userData]
  );

  const tabButton = (key, label, Icon) => (
    <Button
      variant={activeTab === key ? "primary" : "outline-primary"}
      size="sm"
      onClick={() => setActiveTab(key)}
      className="d-flex align-items-center gap-2"
    >
      <Icon />
      {label}
    </Button>
  );

  return (
    <>
      <Row className="g-0">
        <Col
          xs={12}
          className={`bg-transparent sidebar-container ${
            isSidebarCollapsed ? "collapsed" : "hello"
          }`}
          style={{
            width: isSidebarCollapsed ? "90px" : "317px",
            transition: "width 0.3s ease",
          }}
        >
          <Menubar
            isCollapsed={isSidebarCollapsed}
            onToggle={handleSidebarToggle}
          />
        </Col>

        <Col
          className="pt-0 px-0 ps-0 section-container"
          style={{
            width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`,
            transition: "width 0.3s ease",
          }}
        >
          <NavigationBar
            userData={userData}
            isSidebarCollapsed={isSidebarCollapsed}
          />

          <Col className="overflow-auto" style={{ padding: "20px" }}>
            {console.log(
              "ConsentManagementModule mounted",
              new Date().toISOString()
            )}
            <Card className="mb-3">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 style={{ marginBottom: "4px", fontWeight: 700 }}>
                    DPDP Consent Management 2
                  </h5>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Create customizable consent forms, generate public token
                    links, and track submitted consent records.
                  </div>
                </div>
                <Badge bg="success">PUBLIC TOKEN MVP</Badge>
              </Card.Body>
            </Card>

            <div className="d-flex gap-2 mb-3">
              {tabButton(TABS.FORMS, "Consent Forms", FaFileAlt)}
              {tabButton(TABS.LINKS, "Consent Links", FaLink)}
              {tabButton(TABS.DASHBOARD, "Dashboard", FaChartBar)}
            </div>

            {activeTab === TABS.FORMS && (
              <FormsSection
                organizationId={organizationId}
                userData={userData}
                showNotification={showNotification}
              />
            )}

            {activeTab === TABS.LINKS && (
              <LinksSection
                organizationId={organizationId}
                userData={userData}
                showNotification={showNotification}
              />
            )}

            {activeTab === TABS.DASHBOARD && (
              <DashboardSection
                organizationId={organizationId}
                userData={userData}
                showNotification={showNotification}
              />
            )}
          </Col>

          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
        </Col>
      </Row>
    </>
  );
};

const FormsSection = ({ organizationId, userData, showNotification }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [schema, setSchema] = useState(createDefaultSchema());
  const [editingFormId, setEditingFormId] = useState(null);
const [isEditMode, setIsEditMode] = useState(false);

  const [declarationConfirmed, setDeclarationConfirmed] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
const [showPreviewModal, setShowPreviewModal] = useState(false);

 const fetchForms = async () => {
  if (!organizationId) return;

  setLoading(true);
  try {
    const res = await fetchConsentFormsApi(userData || {}, organizationId);
    setForms(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    console.error(err);
    showNotification?.("Failed to load consent forms", "danger");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchForms();
  }, [organizationId]);

 const openCreateModal = () => {
  setEditingFormId(null);
  setIsEditMode(false);
  setFormData(emptyForm);
  setSchema(createDefaultSchema());
  setDeclarationConfirmed(false);
  setShowModal(true);
};
  const saveDraft = async () => {
    if (!formData.formName.trim()) {
      showNotification?.("Form name is required", "warning");
      return;
    }
    if (!declarationConfirmed) {
  showNotification?.("Please confirm the mandatory consent declaration before saving", "warning");
  return;
}

    setSaving(true);
    try {
      const payload = {
        ...formData,
        organizationId,
        createdBy: userData?.email || "admin",
        schemaJson: JSON.stringify(schema),
      };

      const res = isEditMode
  ?await updateConsentFormApi(userData || {}, editingFormId, organizationId, payload)
  : await createConsentFormApi(userData || {}, payload);

     if (!res?.data) throw new Error("Save failed");

  showNotification?.(
  isEditMode ? "Consent form draft updated" : "Consent form saved as draft",
  "success"
);
      setShowModal(false);
      fetchForms();
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to save consent form", "danger");
    } finally {
      setSaving(false);
    }
  };

  const publishForm = async (formId) => {
    try {
      const res = await publishConsentFormApi(userData || {}, formId, organizationId);

      if (!res?.data) throw new Error("Publish failed");

      showNotification?.("Consent form published", "success");
      fetchForms();
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to publish form", "danger");
    }
  };
const parseSchema = (schemaJson) => {
  try {
    return typeof schemaJson === "string"
      ? JSON.parse(schemaJson)
      : schemaJson || {};
  } catch {
    return {};
  }
};

const openPreview = (form) => {
  setPreviewForm({
    ...form,
    parsedSchema: parseSchema(form.schemaJson),
  });
  setShowPreviewModal(true);
};
const openEditModal = (form) => {
  if (form.status === "PUBLISHED") {
    showNotification?.(
      "Published forms cannot be edited. Create a new version instead.",
      "warning"
    );
    return;
  }

  setEditingFormId(form.formId);
  setIsEditMode(true);

  setFormData({
    formName: form.formName || "",
    regulation: form.regulation || "DPDP",
    businessProcess: form.businessProcess || "",
    dataCollectionPurpose: form.dataCollectionPurpose || "",
    formVersion: form.formVersion || "1.0",
    noticeVersion: form.noticeVersion || "1.0",
  });

  setSchema(parseSchema(form.schemaJson));
  setDeclarationConfirmed(true);
  setShowModal(true);
};
  return (
    <>
      <Card>
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <h6 style={{ marginBottom: 4, fontWeight: 700 }}>Consent Forms</h6>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Create form, configure fields and purposes, save draft, then publish.
            </div>
          </div>
          <Button size="sm" onClick={openCreateModal}>
            <FaPlus className="me-2" />
            Create Form
          </Button>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Form Name</th>
                  <th>Regulation</th>
                  <th>Business Process</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {forms.length > 0 ? (
                  forms.map((f) => (
                    <tr key={f.formId}>
                      <td>{f.formName}</td>
                      <td>{f.regulation}</td>
                      <td>{f.businessProcess || "-"}</td>
                      <td>{f.formVersion || "1.0"}</td>
                      <td>
                        <Badge bg={f.status === "PUBLISHED" ? "success" : "secondary"}>
                          {f.status}
                        </Badge>
                      </td>
                      <td>
                        {f.updatedAt
                          ? new Date(f.updatedAt).toLocaleDateString()
                          : "-"}
                      </td>
      <td>
  <div className="d-flex gap-2">
    <Button
      size="sm"
      variant="outline-primary"
      onClick={() => openPreview(f)}
    >
      Preview
    </Button>

    {f.status !== "PUBLISHED" && (
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={() => openEditModal(f)}
      >
        Edit
      </Button>
    )}

    {f.status !== "PUBLISHED" ? (
      <Button
        size="sm"
        variant="outline-success"
        onClick={() => publishForm(f.formId)}
      >
        <FaPaperPlane className="me-1" />
        Publish
      </Button>
    ) : (
      <Badge bg="success" className="align-self-center">
        Ready for Link
      </Badge>
    )}
  </div>
</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No consent forms created yet
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          <Button size="sm" variant="outline-secondary" onClick={fetchForms}>
            <FaRedo className="me-2" />
            Refresh
          </Button>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
  {isEditMode ? "Edit DPDP Consent Form" : "Create DPDP Consent Form"}
</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Card className="mb-3">
            <Card.Body>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Form Name *</Form.Label>
                    <Form.Control
                      value={formData.formName}
                      onChange={(e) =>
                        setFormData({ ...formData, formName: e.target.value })
                      }
                      placeholder="Healthcare DPDP Consent Form"
                    />
                  </Form.Group>
                </Col>
<Row>
<Col md={6}>
  <Form.Group className="mb-3">
    <Form.Label>Company Logo</Form.Label>
    <Form.Control
      type="file"
      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
          setSchema((prev) => ({
            ...prev,
            organizationBranding: {
              ...prev.organizationBranding,
              logoFileName: file.name,
              logoBase64: reader.result,
            },
          }));
        };
        reader.readAsDataURL(file);
      }}
    />

    {schema.organizationBranding.logoFileName && (
      <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
        Uploaded: {schema.organizationBranding.logoFileName}
      </div>
    )}
  </Form.Group>
</Col>

  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Legal Entity Name</Form.Label>
      <Form.Control
        value={schema.organizationBranding.legalEntityName}
        onChange={(e) =>
          setSchema((prev) => ({
            ...prev,
            organizationBranding: {
              ...prev.organizationBranding,
              legalEntityName: e.target.value,
            },
          }))
        }
        placeholder="Company Legal Name"
      />
    </Form.Group>
  </Col>
</Row>

<Row>
  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Default Retention Period</Form.Label>
      <Form.Control
        value={schema.retention.defaultRetentionPeriod}
        onChange={(e) =>
          setSchema((prev) => ({
            ...prev,
            retention: {
              ...prev.retention,
              defaultRetentionPeriod: e.target.value,
            },
          }))
        }
        placeholder="E.g., 7 years / until withdrawn + 3 years"
      />
    </Form.Group>
  </Col>

  <Col md={6}>
    <Form.Group className="mb-3">
      <Form.Label>Retention Basis</Form.Label>
      <Form.Control
        value={schema.retention.retentionBasis}
        onChange={(e) =>
          setSchema((prev) => ({
            ...prev,
            retention: {
              ...prev.retention,
              retentionBasis: e.target.value,
            },
          }))
        }
        placeholder="E.g., legal audit, service delivery, dispute resolution"
      />
    </Form.Group>
  </Col>
</Row>

<Form.Check
  className="mb-3"
  type="checkbox"
  label="Enable Children's Data Declaration"
  checked={schema.childConsent.enabled}
  onChange={(e) =>
    setSchema((prev) => ({
      ...prev,
      childConsent: {
        ...prev.childConsent,
        enabled: e.target.checked,
      },
    }))
  }
/>
{schema.childConsent.enabled && (
  <Card className="mb-3" style={{ border: "1px solid #f59e0b" }}>
    <Card.Body>
      <h6 style={{ fontWeight: 700, marginBottom: "12px" }}>
        Children&apos;s Data Declaration
      </h6>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Guardian Name Field Label</Form.Label>
            <Form.Control
              value={schema.childConsent.guardianNameLabel || "Parent / Guardian Name"}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  childConsent: {
                    ...prev.childConsent,
                    guardianNameLabel: e.target.value,
                  },
                }))
              }
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Guardian Contact Field Label</Form.Label>
            <Form.Control
              value={schema.childConsent.guardianContactLabel || "Guardian Contact Number"}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  childConsent: {
                    ...prev.childConsent,
                    guardianContactLabel: e.target.value,
                  },
                }))
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Relationship Options</Form.Label>
            <Form.Control
              value={
                schema.childConsent.relationshipOptions?.join(", ") ||
                "Father, Mother, Legal Guardian"
              }
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  childConsent: {
                    ...prev.childConsent,
                    relationshipOptions: e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean),
                  },
                }))
              }
              placeholder="Father, Mother, Legal Guardian"
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Guardian Declaration Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={
                schema.childConsent.guardianDeclarationText ||
                "I confirm that I am the parent/legal guardian and provide verifiable parental consent for processing the child&apos;s personal data."
              }
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  childConsent: {
                    ...prev.childConsent,
                    guardianDeclarationText: e.target.value,
                  },
                }))
              }
            />
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
)}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Regulation</Form.Label>
                    <Form.Control value="DPDP" disabled />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Business Process</Form.Label>
                <Form.Control
                  value={formData.businessProcess}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      businessProcess: e.target.value,
                    })
                  }
                  placeholder="Patient Registration / Employee Onboarding / Customer Signup"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Data Collection Purpose</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.dataCollectionPurpose}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dataCollectionPurpose: e.target.value,
                    })
                  }
                  placeholder="Explain why personal data is being collected"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Form Version</Form.Label>
                    <Form.Control
                      value={formData.formVersion}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          formVersion: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Notice Version</Form.Label>
                    <Form.Control
                      value={formData.noticeVersion}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          noticeVersion: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
<Card className="mb-3">
  <Card.Header>
    <strong>Information Collected & Source</strong>
  </Card.Header>
  <Card.Body>
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Data Category</Form.Label>
          <Form.Select
            value={schema.informationCollected.dataCategory}
            onChange={(e) =>
              setSchema((prev) => ({
                ...prev,
                informationCollected: {
                  ...prev.informationCollected,
                  dataCategory: e.target.value,
                },
              }))
            }
          >
            {schema.informationCollected.availableDataCategories.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Source of Collection</Form.Label>
          <Form.Select
            value={schema.informationCollected.sourceOfCollection}
            onChange={(e) =>
              setSchema((prev) => ({
                ...prev,
                informationCollected: {
                  ...prev.informationCollected,
                  sourceOfCollection: e.target.value,
                },
              }))
            }
          >
            {schema.informationCollected.availableSources.map((x) => (
              <option key={x} value={x}>{x}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    </Row>
  </Card.Body>
</Card>

<Card className="mb-3">
  <Card.Header>
    <strong>Legal Basis for Processing</strong>
  </Card.Header>
  <Card.Body>
    <Form.Group className="mb-3">
      <Form.Label>Legal Basis</Form.Label>
      <Form.Select
        value={schema.legalBasis.basis}
        onChange={(e) =>
          setSchema((prev) => ({
            ...prev,
            legalBasis: {
              ...prev.legalBasis,
              basis: e.target.value,
            },
          }))
        }
      >
        {schema.legalBasis.availableBasis.map((x) => (
          <option key={x} value={x}>{x}</option>
        ))}
      </Form.Select>
    </Form.Group>

    <Form.Group>
      <Form.Label>Legal Basis Description</Form.Label>
      <Form.Control
        as="textarea"
        rows={3}
        value={schema.legalBasis.description}
        onChange={(e) =>
          setSchema((prev) => ({
            ...prev,
            legalBasis: {
              ...prev.legalBasis,
              description: e.target.value,
            },
          }))
        }
      />
    </Form.Group>
  </Card.Body>
</Card>

<Card className="mb-3">
  <Card.Header>
    <strong>Third-Party Recipients</strong>
  </Card.Header>
  <Card.Body>
    <Form.Check
      className="mb-3"
      type="checkbox"
      label="Enable third-party recipient disclosure"
      checked={schema.thirdPartyRecipients.enabled}
      onChange={(e) =>
        setSchema((prev) => ({
          ...prev,
          thirdPartyRecipients: {
            ...prev.thirdPartyRecipients,
            enabled: e.target.checked,
          },
          noThirdPartySharing: !e.target.checked,
        }))
      }
    />

    {schema.thirdPartyRecipients.enabled && (
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Recipient Category</Form.Label>
            <Form.Select
              value={schema.thirdPartyRecipients.recipientCategory}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  thirdPartyRecipients: {
                    ...prev.thirdPartyRecipients,
                    recipientCategory: e.target.value,
                  },
                }))
              }
            >
              <option value="">Select...</option>
              {schema.thirdPartyRecipients.availableRecipientCategories.map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Purpose</Form.Label>
            <Form.Control
              value={schema.thirdPartyRecipients.purpose}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  thirdPartyRecipients: {
                    ...prev.thirdPartyRecipients,
                    purpose: e.target.value,
                  },
                }))
              }
              placeholder="E.g., hosting, payment, support"
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Data Shared</Form.Label>
            <Form.Control
              value={schema.thirdPartyRecipients.dataShared}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  thirdPartyRecipients: {
                    ...prev.thirdPartyRecipients,
                    dataShared: e.target.value,
                  },
                }))
              }
              placeholder="E.g., identity data, logs"
            />
          </Form.Group>
        </Col>
      </Row>
    )}
  </Card.Body>
</Card>

<Card className="mb-3">
  <Card.Header>
    <strong>Consent Withdrawal</strong>
  </Card.Header>

  <Card.Body>
    <Form.Check
      className="mb-3"
      type="checkbox"
      label="Enable consent withdrawal section"
      checked={schema.withdrawal.enabled}
      onChange={(e) =>
        setSchema((prev) => ({
          ...prev,
          withdrawal: {
            ...prev.withdrawal,
            enabled: e.target.checked,
          },
        }))
      }
    />

    {schema.withdrawal.enabled && (
      <Card style={{ background: "#F8FAFC", border: "1px solid #E5E7EB" }}>
        <Card.Body>
          <h6 style={{ fontWeight: 700, marginBottom: "6px" }}>
            Consent Withdrawal Options
          </h6>

          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "18px" }}>
            You can withdraw your consent at any time as per your rights under DPDP Act 2023.
          </p>

          <div className="d-flex flex-column gap-3">
            <Form.Check
              type="checkbox"
              label={
                <div>
                  <strong>Withdraw All Consent</strong>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Revoke all data processing permissions
                  </div>
                </div>
              }
              checked={schema.withdrawal.allowWithdrawAll}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  withdrawal: {
                    ...prev.withdrawal,
                    allowWithdrawAll: e.target.checked,
                  },
                }))
              }
            />

            <Form.Check
              type="checkbox"
              label={
                <div>
                  <strong>Withdraw Marketing Consent</strong>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Stop receiving marketing communications
                  </div>
                </div>
              }
              checked={schema.withdrawal.allowWithdrawOptional}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  withdrawal: {
                    ...prev.withdrawal,
                    allowWithdrawOptional: e.target.checked,
                  },
                }))
              }
            />

            <Form.Check
              type="checkbox"
              label={
                <div>
                  <strong>Withdraw Third-Party Sharing</strong>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Stop sharing data with third parties
                  </div>
                </div>
              }
              checked={schema.withdrawal.allowWithdrawSpecific}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  withdrawal: {
                    ...prev.withdrawal,
                    allowWithdrawSpecific: e.target.checked,
                  },
                }))
              }
            />

            <Form.Check
              type="checkbox"
              label={
                <div>
                  <strong>Consent Withdrawal Notice</strong>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {schema.withdrawal.withdrawalText}
                  </div>
                </div>
              }
              checked={schema.withdrawal.withdrawalNoticeAccepted || false}
              onChange={(e) =>
                setSchema((prev) => ({
                  ...prev,
                  withdrawal: {
                    ...prev.withdrawal,
                    withdrawalNoticeAccepted: e.target.checked,
                  },
                }))
              }
            />
          </div>
        </Card.Body>
      </Card>
    )}
  </Card.Body>
</Card>
          <BuilderSection schema={schema} setSchema={setSchema} />
        </Modal.Body>

      <Modal.Footer className="d-block">
  <Card style={{ background: "#F8FAFC", border: "1px solid #E5E7EB" }}>
    <Card.Body>
      <h6 style={{ fontWeight: 700, marginBottom: "10px" }}>
        Consent Declaration
      </h6>

      <Form.Check
        type="checkbox"
        checked={declarationConfirmed}
        onChange={(e) => setDeclarationConfirmed(e.target.checked)}
        label={
          schema.consentDeclaration.declarationText ||
          "I confirm that the mandatory consent declaration will be shown at the end of the public consent form."
        }
      />
    </Card.Body>
  </Card>

  <div className="d-flex justify-content-end gap-2 mt-3">
    <Button variant="secondary" onClick={() => setShowModal(false)}>
      Cancel
    </Button>

    <Button onClick={saveDraft} disabled={saving || !declarationConfirmed}>
      <FaSave className="me-2" />
      {saving ? "Saving..." : isEditMode ? "Update Draft" : "Save Draft"}
    </Button>
  </div>
</Modal.Footer>
      </Modal>
      <Modal
  show={showPreviewModal}
  onHide={() => setShowPreviewModal(false)}
  size="xl"
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Preview Consent Form</Modal.Title>
  </Modal.Header>

  <Modal.Body style={{ background: "#F8FAFC" }}>
    {!previewForm ? null : (
      <>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex align-items-center gap-3">
              {previewForm.parsedSchema?.organizationBranding?.logoBase64 && (
                <img
                  src={previewForm.parsedSchema.organizationBranding.logoBase64}
                  alt="Company Logo"
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: "contain",
                    border: "1px solid #E5E7EB",
                    borderRadius: 12,
                    padding: 6,
                    background: "#fff",
                  }}
                />
              )}

              <div>
                <h5 style={{ fontWeight: 800, marginBottom: 4 }}>
                  {previewForm.formName}
                </h5>
                <div style={{ fontSize: 13, color: "#64748B" }}>
                  Data Fiduciary:{" "}
                  {previewForm.parsedSchema?.organizationBranding?.legalEntityName || "-"}
                </div>
                <div style={{ fontSize: 13, color: "#64748B" }}>
                  Regulation: {previewForm.regulation || "DPDP"}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Information Collected & Source</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <strong>Data Category</strong>
                <div>{previewForm.parsedSchema?.informationCollected?.dataCategory || "-"}</div>
              </Col>
              <Col md={6}>
                <strong>Source of Collection</strong>
                <div>{previewForm.parsedSchema?.informationCollected?.sourceOfCollection || "-"}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Legal Basis & Retention</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <strong>Legal Basis</strong>
                <div>{previewForm.parsedSchema?.legalBasis?.basis || "-"}</div>
              </Col>
              <Col md={4}>
                <strong>Retention Period</strong>
                <div>{previewForm.parsedSchema?.retention?.defaultRetentionPeriod || "-"}</div>
              </Col>
              <Col md={4}>
                <strong>Retention Basis</strong>
                <div>{previewForm.parsedSchema?.retention?.retentionBasis || "-"}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Data Principal Fields</strong>
          </Card.Header>
          <Card.Body>
            {(previewForm.parsedSchema?.sections || [])
              .find((s) => s.key === "DATA_PRINCIPAL")
              ?.fields?.map((field) => (
                <Form.Group className="mb-3" key={field.id}>
                  <Form.Label>
                    {field.label}
                    {field.required && <span className="text-danger ms-1">*</span>}
                  </Form.Label>
                  <Form.Control
                    disabled
                    placeholder={field.placeholder}
                    type={field.type === "tel" ? "text" : field.type}
                  />
                </Form.Group>
              ))}
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Purpose-wise Consent</strong>
          </Card.Header>
          <Card.Body>
            {(previewForm.parsedSchema?.purposes || []).map((purpose) => (
              <div
                key={purpose.id}
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 10,
                  background: "#fff",
                }}
              >
                <div className="d-flex justify-content-between">
                  <strong>{purpose.name}</strong>
                  <Badge bg={purpose.mandatory ? "primary" : "secondary"}>
                    {purpose.mandatory ? "Mandatory" : "Optional"}
                  </Badge>
                </div>
                <div style={{ fontSize: 12, color: "#64748B" }}>
                  {purpose.description}
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Consent Declaration</strong>
          </Card.Header>
          <Card.Body>
            <Form.Check
              disabled
              type="checkbox"
              label={
                previewForm.parsedSchema?.consentDeclaration?.declarationText ||
                "Consent declaration"
              }
            />
          </Card.Body>
        </Card>

        {previewForm.parsedSchema?.withdrawal?.enabled && (
          <Card>
            <Card.Header>
              <strong>Consent Withdrawal Options</strong>
            </Card.Header>
            <Card.Body>
              <p style={{ fontSize: 13, color: "#64748B" }}>
                {previewForm.parsedSchema.withdrawal.withdrawalText}
              </p>

              {previewForm.parsedSchema.withdrawal.allowWithdrawAll && (
                <Form.Check disabled label="Withdraw All Consent" />
              )}

              {previewForm.parsedSchema.withdrawal.allowWithdrawOptional && (
                <Form.Check disabled label="Withdraw Optional Consent" />
              )}

              {previewForm.parsedSchema.withdrawal.allowWithdrawSpecific && (
                <Form.Check disabled label="Withdraw Specific Purpose Consent" />
              )}
            </Card.Body>
          </Card>
        )}
      </>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
      Close
    </Button>

    {previewForm?.status !== "PUBLISHED" && (
      <Button
        variant="success"
        onClick={() => {
          publishForm(previewForm.formId);
          setShowPreviewModal(false);
        }}
      >
        Publish Form
      </Button>
    )}
  </Modal.Footer>
</Modal>
    </>
  );
};

const BuilderSection = ({ schema, setSchema }) => {
  const dataSection = schema.sections.find((s) => s.key === "DATA_PRINCIPAL");

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: "New Field",
      type: "text",
      required: false,
      sensitive: false,
      placeholder: "",
      options: [],
    };

    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.key === "DATA_PRINCIPAL"
          ? { ...section, fields: [...(section.fields || []), newField] }
          : section
      ),
    }));
  };

  const updateField = (fieldId, updates) => {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.key === "DATA_PRINCIPAL"
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, ...updates } : field
              ),
            }
          : section
      ),
    }));
  };

  const removeField = (fieldId) => {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.key === "DATA_PRINCIPAL"
          ? {
              ...section,
              fields: section.fields.filter((field) => field.id !== fieldId),
            }
          : section
      ),
    }));
  };

  const addPurpose = () => {
    const purpose = {
      id: `purpose_${Date.now()}`,
      name: "New Purpose",
      description: "",
      mandatory: false,
      defaultEnabled: false,
    };

    setSchema((prev) => ({
      ...prev,
      purposes: [...prev.purposes, purpose],
    }));
  };

  const updatePurpose = (purposeId, updates) => {
    setSchema((prev) => ({
      ...prev,
      purposes: prev.purposes.map((p) =>
        p.id === purposeId ? { ...p, ...updates } : p
      ),
    }));
  };

  const removePurpose = (purposeId) => {
    setSchema((prev) => ({
      ...prev,
      purposes: prev.purposes.filter((p) => p.id !== purposeId),
    }));
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Data Principal Fields</strong>
          <Button size="sm" onClick={addField}>
            <FaPlus className="me-1" />
            Add Field
          </Button>
        </Card.Header>
        <Card.Body>
          {(dataSection?.fields || []).map((field) => (
            <Card key={field.id} className="mb-3">
              <Card.Body>
              <Row className="align-items-end g-3">
  <Col md={3}>
    <Form.Group>
     <Form.Label>Field Name</Form.Label>
      <Form.Control
        value={field.label}
        onChange={(e) =>
          updateField(field.id, { label: e.target.value })
        }
      />
    </Form.Group>
  </Col>

  <Col md={2}>
    <Form.Group>
     <Form.Label>Field Format</Form.Label>
      <Form.Select
        value={field.type}
        onChange={(e) =>
          updateField(field.id, {
            type: e.target.value,
            options:
              ["dropdown", "radio"].includes(e.target.value)
                ? field.options || []
                : [],
          })
        }
      >
      {FIELD_TYPES.map((type) => (
  <option key={type} value={type}>
    {{
      text: "Short Text",
      email: "Email Address",
      tel: "Phone Number",
      date: "Date",
      number: "Number",
      textarea: "Long Text",
      dropdown: "Dropdown List",
      radio: "Single Choice",
      checkbox: "Checkbox",
    }[type] || type}
  </option>
))}
      </Form.Select>
    </Form.Group>
  </Col>

  <Col md={3}>
    <Form.Group>
      <Form.Label>Help Text</Form.Label>
      <Form.Control
        value={field.placeholder || ""}
        onChange={(e) =>
          updateField(field.id, { placeholder: e.target.value })
        }
      />
    </Form.Group>
  </Col>

  <Col md={3}>
    <div
      className="d-flex align-items-center gap-3"
      style={{ minHeight: "38px" }}
    >
      <Form.Check
        type="checkbox"
        label="Required"
        checked={!!field.required}
        onChange={(e) =>
          updateField(field.id, { required: e.target.checked })
        }
      />

      <Form.Check
        type="checkbox"
        label="Sensitive"
        checked={!!field.sensitive}
        onChange={(e) =>
          updateField(field.id, { sensitive: e.target.checked })
        }
      />
    </div>
  </Col>

  <Col md={1} className="text-end">
    <Button
      size="sm"
      variant="outline-danger"
      onClick={() => removeField(field.id)}
      title="Delete field"
    >
      <FaTrash />
    </Button>
  </Col>
</Row>

                {["dropdown", "radio"].includes(field.type) && (
                  <Form.Group className="mt-3">
                    <Form.Label>Options, comma separated</Form.Label>
                    <Form.Control
                      value={(field.options || []).join(", ")}
                      onChange={(e) =>
                        updateField(field.id, {
                          options: e.target.value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="Option 1, Option 2, Option 3"
                    />
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          ))}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Purpose-wise Consent</strong>
          <Button size="sm" onClick={addPurpose}>
            <FaPlus className="me-1" />
            Add Purpose
          </Button>
        </Card.Header>
        <Card.Body>
          {(schema.purposes || []).map((purpose) => (
            <Card key={purpose.id} className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Purpose Name</Form.Label>
                      <Form.Control
                        value={purpose.name}
                        onChange={(e) =>
                          updatePurpose(purpose.id, { name: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        value={purpose.description || ""}
                        onChange={(e) =>
                          updatePurpose(purpose.id, {
                            description: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Check
                      className="mt-4"
                      label="Mandatory"
                      checked={!!purpose.mandatory}
                      onChange={(e) =>
                        updatePurpose(purpose.id, {
                          mandatory: e.target.checked,
                          defaultEnabled: e.target.checked
                            ? true
                            : purpose.defaultEnabled,
                        })
                      }
                    />
                  </Col>

                  <Col md={2}>
                    <Form.Check
                      className="mt-4"
                      label="Default"
                      checked={!!purpose.defaultEnabled}
                      disabled={!!purpose.mandatory}
                      onChange={(e) =>
                        updatePurpose(purpose.id, {
                          defaultEnabled: e.target.checked,
                        })
                      }
                    />
                  </Col>

                  <Col md={1}>
                    <Button
                      className="mt-4"
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removePurpose(purpose.id)}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}
        </Card.Body>
      </Card>
    </>
  );
};

const LinksSection = ({ organizationId,  userData,showNotification }) => {
  const [forms, setForms] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [linkName, setLinkName] = useState("");

  const fetchForms = async () => {
    if (!organizationId) return;

   const res = await fetchConsentFormsApi(userData || {}, organizationId);
const data = res.data;
setForms(Array.isArray(data) ? data.filter((f) => f.status === "PUBLISHED") : []);
   
  };

  const fetchLinks = async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
     const res = await fetchConsentLinksApi(userData || {}, organizationId);
     setLinks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to load consent links", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
    fetchLinks();
  }, [organizationId]);

  const generateLink = async () => {
    if (!selectedFormId) {
      showNotification?.("Please select a published form", "warning");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        organizationId,
        formId: selectedFormId,
        linkName,
        publicBaseUrl: "https://demo.securecloud.tech",
        createdBy: "admin",
      };

      const res = await generateConsentLinkApi(userData || {}, payload);

      if (!res?.data) throw new Error("Failed to generate link");

      showNotification?.("Consent link generated successfully", "success");
      setShowModal(false);
      setSelectedFormId("");
      setLinkName("");
      fetchLinks();
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to generate consent link", "danger");
    } finally {
      setGenerating(false);
    }
  };

  const revokeLink = async (token) => {
    if (!window.confirm("Are you sure you want to revoke this consent link?")) {
      return;
    }

    try {
      const res = await revokeConsentLinkApi(userData || {}, token);
    if (!res?.data) throw new Error("Revoke failed");

      showNotification?.("Consent link revoked", "success");
      fetchLinks();
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to revoke link", "danger");
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showNotification?.("Link copied", "success");
    } catch {
      showNotification?.("Could not copy link", "warning");
    }
  };

  return (
    <>
      <Card>
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <h6 style={{ marginBottom: 4, fontWeight: 700 }}>Consent Links</h6>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Generate public token links for published forms.
            </div>
          </div>

          <Button size="sm" onClick={() => setShowModal(true)}>
            <FaLink className="me-2" />
            Generate Link
          </Button>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Link Name</th>
                  <th>Form ID</th>
                  <th>Token URL</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.length > 0 ? (
                  links.map((l) => {
                    const url = l.publicUrl || publicConsentUrl(l.token);

                    return (
                      <tr key={l.token}>
                        <td>{l.linkName || "-"}</td>
                        <td>
                          <small>{l.formId}</small>
                        </td>
                        <td style={{ maxWidth: 360 }}>
                          <small className="text-muted">{url}</small>
                        </td>
                        <td>
                          <Badge bg={l.status === "ACTIVE" ? "success" : "danger"}>
                            {l.status}
                          </Badge>
                        </td>
                        <td>
                          {l.createdAt
                            ? new Date(l.createdAt).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => copyLink(url)}
                            >
                              <FaCopy />
                            </Button>

                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => window.open(url, "_blank")}
                            >
                              <FaExternalLinkAlt />
                            </Button>

                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={l.status !== "ACTIVE"}
                              onClick={() => revokeLink(l.token)}
                            >
                              <FaBan />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No consent links generated yet
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          <Button size="sm" variant="outline-secondary" onClick={fetchLinks}>
            <FaRedo className="me-2" />
            Refresh
          </Button>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Generate Consent Link</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Published Form</Form.Label>
            <Form.Select
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
            >
              <option value="">Select form...</option>
              {forms.map((f) => (
                <option key={f.formId} value={f.formId}>
                  {f.formName} · v{f.formVersion}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Link Name</Form.Label>
            <Form.Control
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              placeholder="Healthcare onboarding consent link"
            />
          </Form.Group>

          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Anyone with this public token link can open and submit the consent form.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>

          <Button onClick={generateLink} disabled={generating}>
            {generating ? "Generating..." : "Generate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const DashboardSection = ({ organizationId,  userData, showNotification }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
const [selectedSubmission, setSelectedSubmission] = useState(null);
const [showViewModal, setShowViewModal] = useState(false);

  const fetchSubmissions = async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
     const res = await fetchConsentSubmissionsApi(userData || {}, organizationId);
     setSubmissions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification?.("Failed to load consent dashboard", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [organizationId]);

  const activeCount = submissions.filter((s) => s.status === "ACTIVE").length;

  return (
    <>
      <Row className="mb-3">
        <Col md={3}>
          <Card>
            <Card.Body>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Total Consents</div>
              <h4>{submissions.length}</h4>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card>
            <Card.Body>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Active</div>
              <h4 className="text-success">{activeCount}</h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Submitted Consents</strong>
          <Button size="sm" variant="outline-secondary" onClick={fetchSubmissions}>
            <FaRedo className="me-2" />
            Refresh
          </Button>
        </Card.Header>

        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Consent ID</th>
                  <th>Form ID</th>
                  <th>Status</th>
                  <th>Submitted At</th>
                  <th>Region</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {submissions.length > 0 ? (
                  submissions.map((s) => (
                    <tr key={s.consentId}>
                      <td>{s.consentId}</td>
                      <td>{s.formId}</td>
                      <td>
                        <Badge bg={s.status === "ACTIVE" ? "success" : "secondary"}>
                          {s.status}
                        </Badge>
                      </td>
                      <td>
                        {s.submittedAt
                          ? new Date(s.submittedAt).toLocaleString()
                          : "-"}
                      </td>
                      <td>{s.region || "-"}</td>
                      <td>
  <Button
    size="sm"
    variant="outline-primary"
    onClick={() => {
      setSelectedSubmission(s);
      setShowViewModal(true);
    }}
  >
    View
  </Button>
</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No submitted consents found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
            </Card>

   <Modal
  show={showViewModal}
  onHide={() => setShowViewModal(false)}
  size="xl"
  centered
>
  <Modal.Header closeButton>
    <Modal.Title style={{ fontWeight: 800 }}>
      Submitted Consent Record
    </Modal.Title>
  </Modal.Header>

  <Modal.Body style={{ background: "#F8FAFC", padding: "20px" }}>
    {!selectedSubmission ? null : (() => {
      let values = {};
      let purposes = {};
      let childData = {};
      let formSchema = {};

      try {
        values =
          typeof selectedSubmission.submittedValuesJson === "string"
            ? JSON.parse(selectedSubmission.submittedValuesJson)
            : selectedSubmission.submittedValuesJson || {};
      } catch {
        values = {};
      }

      try {
        purposes =
          typeof selectedSubmission.purposeChoicesJson === "string"
            ? JSON.parse(selectedSubmission.purposeChoicesJson)
            : selectedSubmission.purposeChoicesJson || {};
      } catch {
        purposes = {};
      }

      try {
        childData =
          typeof selectedSubmission.childDataJson === "string"
            ? JSON.parse(selectedSubmission.childDataJson)
            : selectedSubmission.childDataJson || {};
      } catch {
        childData = {};
      }

      try {
        formSchema =
          typeof selectedSubmission.schemaJson === "string"
            ? JSON.parse(selectedSubmission.schemaJson)
            : selectedSubmission.schemaJson || {};
      } catch {
        formSchema = {};
      }

      const branding = formSchema?.organizationBranding || {};
      const informationCollected = formSchema?.informationCollected || {};
      const legalBasis = formSchema?.legalBasis || {};
      const retention = formSchema?.retention || {};
      const thirdParty = formSchema?.thirdPartyRecipients || {};
      const declaration = formSchema?.consentDeclaration || {};
      const withdrawal = formSchema?.withdrawal || {};
      const schemaPurposes = formSchema?.purposes || [];

      const formatLabel = (key) =>
        key
          .replace(/_/g, " ")
          .replace(/([A-Z])/g, " $1")
          .replace(/\b\w/g, (c) => c.toUpperCase())
          .trim();

      const formatDate = (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return Number.isNaN(d.getTime()) ? date : d.toLocaleString();
      };

      const valueByKey = (key) => values?.[key] || "-";

      const purposeLabel = (purposeKey) => {
        const found = schemaPurposes.find((p) => p.id === purposeKey);
        return found?.name || formatLabel(purposeKey);
      };

      const purposeType = (purposeKey) => {
        const found = schemaPurposes.find((p) => p.id === purposeKey);
        return found?.mandatory ? "Mandatory" : "Optional";
      };

      return (
        <>
          {/* Header */}
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body style={{ borderLeft: "4px solid #0d6efd" }}>
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: 86,
                      height: 86,
                      border: "1px solid #E5E7EB",
                      borderRadius: 14,
                      background: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 8,
                    }}
                  >
                    {branding.logoBase64 ? (
                      <img
                        src={branding.logoBase64}
                        alt="Company Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 12, color: "#94A3B8" }}>
                        LOGO
                      </span>
                    )}
                  </div>

                  <div>
                    <h5 style={{ fontWeight: 800, marginBottom: 4 }}>
                      {selectedSubmission.formName || "Submitted Consent Form"}
                    </h5>

                    <div style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>
                      Data Fiduciary: {branding.legalEntityName || "-"}
                    </div>

                    <div className="mt-3" style={{ fontSize: 13, color: "#475569" }}>
                      <div>Regulation: India DPDP Act, 2023</div>
                      <div>
                        Business Process:{" "}
                        {selectedSubmission.businessProcess ||
                          formSchema?.businessProcess ||
                          "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-end">
                  <Badge
                    bg={selectedSubmission.status === "ACTIVE" ? "success" : "secondary"}
                    style={{ padding: "8px 14px", borderRadius: 20 }}
                  >
                    {selectedSubmission.status || "ACTIVE"}
                  </Badge>

                  <div className="mt-4" style={{ fontSize: 12, color: "#64748B" }}>
                    Submitted On
                  </div>
                  <div style={{ fontWeight: 800 }}>
                    {formatDate(selectedSubmission.submittedAt)}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Consent Summary */}
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <h6 style={{ fontWeight: 800, marginBottom: 14 }}>
                Consent Summary
              </h6>

              <Row className="g-3">
                <Col md={3}>
                  <InfoBox label="Consent ID" value={selectedSubmission.consentId} />
                </Col>
                <Col md={3}>
                  <InfoBox label="Data Principal" value={valueByKey("fullName")} />
                </Col>
                <Col md={3}>
                  <InfoBox label="Email Address" value={valueByKey("email")} />
                </Col>
                <Col md={3}>
                  <InfoBox label="Mobile Number" value={valueByKey("mobile")} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Data Principal */}
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <h6 style={{ fontWeight: 800, marginBottom: 14 }}>
                Data Principal Information
              </h6>

              <Row className="g-3">
                <Col md={6}>
                  <CleanTable
                    rows={[
                      ["Full Name", valueByKey("fullName")],
                      ["Date of Birth", valueByKey("dob")],
                      ["Email Address", valueByKey("email")],
                    ]}
                  />
                </Col>

                <Col md={6}>
                  <CleanTable
                    rows={[
                      ["Mobile Number", valueByKey("mobile")],
                      ["Residential Address", valueByKey("residentialAddress")],
                      ["Aadhaar/PAN Last 4", valueByKey("aadhaarPanLast4")],
                    ]}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* DPDP Context */}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h6 style={{ fontWeight: 800 }}>Information Collected & Source</h6>
                  <CleanTable
                    rows={[
                      ["Data Category", informationCollected.dataCategory || "-"],
                      ["Source of Collection", informationCollected.sourceOfCollection || "-"],
                      ["Collection Method", "Online / Web Form"],
                    ]}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h6 style={{ fontWeight: 800 }}>Legal Basis & Retention</h6>
                  <CleanTable
                    rows={[
                      ["Legal Basis", legalBasis.basis || "-"],
                      ["Retention Period", retention.defaultRetentionPeriod || "-"],
                      ["Retention Basis", retention.retentionBasis || "-"],
                    ]}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h6 style={{ fontWeight: 800 }}>Third-Party Sharing</h6>

                  {thirdParty.enabled ? (
                    <CleanTable
                      rows={[
                        ["Recipient", thirdParty.recipientCategory || "-"],
                        ["Purpose", thirdParty.purpose || "-"],
                        ["Data Shared", thirdParty.dataShared || "-"],
                      ]}
                    />
                  ) : (
                    <div
                      style={{
                        background: "#ECFDF5",
                        border: "1px solid #BBF7D0",
                        borderRadius: 12,
                        padding: 18,
                        color: "#166534",
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      No Third-Party Sharing
                      <div style={{ fontSize: 12, fontWeight: 500, marginTop: 6 }}>
                        Your data is not shared with any third-party recipients.
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Purposes */}
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <h6 style={{ fontWeight: 800, marginBottom: 14 }}>
                Purpose-wise Consent
              </h6>

              <Table responsive borderless className="mb-0">
                <thead>
                  <tr style={{ color: "#64748B", fontSize: 13 }}>
                    <th>Purpose</th>
                    <th>Type</th>
                    <th>Consent Given</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(purposes).map(([key, value]) => (
                    <tr key={key} style={{ borderTop: "1px solid #E5E7EB" }}>
                      <td style={{ fontWeight: 700 }}>{purposeLabel(key)}</td>
                      <td>
                        <Badge
                          bg={purposeType(key) === "Mandatory" ? "primary" : "warning"}
                          style={{ borderRadius: 20 }}
                        >
                          {purposeType(key)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={value ? "success" : "secondary"}>
                          {value ? "Accepted" : "Not Accepted"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Declaration and Withdrawal */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h6 style={{ fontWeight: 800 }}>Consent Declaration</h6>
                  <div
                    style={{
                      background: "#ECFDF5",
                      border: "1px solid #BBF7D0",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <Badge bg="success" className="mb-2">
                      Declaration Accepted
                    </Badge>
                    <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                      {declaration.declarationText || "-"}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <h6 style={{ fontWeight: 800 }}>Consent Withdrawal</h6>
                  <div
                    style={{
                      background: "#F5F3FF",
                      border: "1px solid #DDD6FE",
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <Badge bg={withdrawal.enabled ? "success" : "secondary"} className="mb-2">
                      {withdrawal.enabled ? "Withdrawal is Allowed" : "Withdrawal Not Enabled"}
                    </Badge>
                    <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                      {withdrawal.withdrawalText || "-"}
                    </div>

                    {withdrawal.enabled && (
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {withdrawal.allowWithdrawAll && (
                          <Badge bg="light" text="dark">Withdraw All Consent</Badge>
                        )}
                        {withdrawal.allowWithdrawOptional && (
                          <Badge bg="light" text="dark">Withdraw Optional Consent</Badge>
                        )}
                        {withdrawal.allowWithdrawSpecific && (
                          <Badge bg="light" text="dark">Withdraw Specific Purpose</Badge>
                        )}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Audit */}
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 style={{ fontWeight: 800, marginBottom: 14 }}>Audit Evidence</h6>

              <Row className="g-3">
                <Col md={2}>
                  <InfoBox label="IP Address" value={selectedSubmission.ipAddress || "-"} />
                </Col>
                <Col md={2}>
                  <InfoBox label="Region" value={selectedSubmission.region || "-"} />
                </Col>
                <Col md={4}>
                  <InfoBox label="User Agent" value={selectedSubmission.userAgent || "-"} />
                </Col>
                <Col md={2}>
                  <InfoBox label="Form Version" value={selectedSubmission.formVersion || "-"} />
                </Col>
                <Col md={2}>
                  <InfoBox label="Notice Version" value={selectedSubmission.noticeVersion || "-"} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      );
    })()}
  </Modal.Body>

  <Modal.Footer style={{ background: "#F8FAFC" }}>
    <Button variant="outline-secondary" onClick={() => window.print()}>
      Print
    </Button>
    <Button variant="outline-primary">
      Download PDF
    </Button>
    <Button variant="primary" onClick={() => setShowViewModal(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>
    </>
  );
};
const InfoBox = ({ label, value }) => (
  <div
    style={{
      background: "#F8FAFC",
      border: "1px solid #E5E7EB",
      borderRadius: 12,
      padding: "12px 14px",
      minHeight: 74,
    }}
  >
    <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontWeight: 800, color: "#0F172A", wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);

const CleanTable = ({ rows = [] }) => (
  <Table borderless size="sm" className="mb-0">
    <tbody>
      {rows.map(([label, value]) => (
        <tr key={label} style={{ borderBottom: "1px dashed #E5E7EB" }}>
          <td style={{ fontWeight: 700, color: "#0F172A", width: "45%" }}>
            {label}
          </td>
          <td style={{ color: "#334155", wordBreak: "break-word" }}>
            {value || "-"}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
export default ConsentManagementModule;